/**
 * Language-aware display labels for values the portal stores as neutral codes
 * or in one fixed language: ISO country codes, English city names and the
 * Estonian gender words. Pure functions — the stored values never change, only
 * what the viewer sees.
 */
import { t } from '../../../../common/i18n.js'
import type { Language } from '../../../../common/types/i18n.js'

/**
 * Localised country name from an ISO 3166-1 alpha-2 code, e.g. "EE" → "Eesti"
 * / "Estonia". Falls back to the code itself when Intl.DisplayNames is missing
 * (old browsers) or rejects the code.
 */
const regionNames = new Map<Language, Intl.DisplayNames | null>()

const regionNamesFor = (lang: Language): Intl.DisplayNames | null => {
    if (!regionNames.has(lang)) {
        try {
            regionNames.set(lang, new Intl.DisplayNames([lang], { type: 'region' }))
        } catch {
            regionNames.set(lang, null)
        }
    }
    return regionNames.get(lang) ?? null
}

export const countryLabel = (code: string, lang: Language): string => {
    if (!code) return ''
    try {
        return regionNamesFor(lang)?.of(code) ?? code
    } catch {
        return code
    }
}

/** English city name → Estonian exonym, for the cities whose name differs. */
const CITY_NAMES_ET: Record<string, string> = {
    Helsinki: 'Helsingi',
    Riga: 'Riia',
    Copenhagen: 'Kopenhaagen',
    Munich: 'München',
    Paris: 'Pariis',
    Rome: 'Rooma',
    Milan: 'Milano',
    Warsaw: 'Varssavi',
    Lisbon: 'Lissabon',
    Prague: 'Praha',
    Vienna: 'Viin',
    Zurich: 'Zürich',
    Brussels: 'Brüssel',
    Kyiv: 'Kiiev',
}

/** Display name for a city stored under its English name. */
export const cityLabel = (value: string, lang: Language): string =>
    lang === 'et' ? (CITY_NAMES_ET[value] ?? value) : value

/**
 * Display label for a stored gender value. The portal stores the Estonian
 * words (`Mees` / `Naine` / `Muu`); Humaans-imported rows may still carry the
 * English ones. Anything else is shown as stored.
 */
export const genderLabel = (value: string, lang: Language): string => {
    const g = t.portal.profile
    switch (normalizeGender(value)) {
        case 'Mees':
            return g.genderMale(lang)
        case 'Naine':
            return g.genderFemale(lang)
        case 'Muu':
            return g.genderOther(lang)
        default:
            return value
    }
}

/**
 * Stored gender value → the portal's own vocabulary (`Mees` / `Naine` / `Muu`),
 * so a Humaans-imported `Male` selects the right option in the editor and is
 * saved in the portal form. Unknown values pass through unchanged.
 */
export const normalizeGender = (value: string): string => {
    switch (value.trim().toLowerCase()) {
        case 'mees':
        case 'male':
            return 'Mees'
        case 'naine':
        case 'female':
            return 'Naine'
        case 'muu':
        case 'other':
        case 'non-binary':
            return 'Muu'
        default:
            return value
    }
}
